import React, { useEffect, useState } from "react";

import { Checkbox } from "components/ui/checkbox";
import { Button } from "components/ui/button";

import ProgressCircle from "components/generic/ProgressCircle";
import Badge from "components/generic/Badge";
import FeatureList from "components/generic/FeatureList";

import { Circle, MapPin, Route, Aperture, Target, ChevronRight, Star } from "lucide-react";
import { roleOptions, skillOptions } from "data/staticData";

const availabilityBadgeProps = {
  available: {
    text: 'Available',
    textColor: 'text-green-900',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
  },
  unavailable: {
    text: 'Unavailable',
    textColor: 'text-red-600',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
  },
  busy: {
    text: 'On Task',
    textColor: 'text-yellow-600',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
  },
};

const formatLocation = (locationStr) => {
  try {
    if (!locationStr) return "Unknown Location";

    const locObj = JSON.parse(locationStr);
    const fullAddress = locObj.address;

    if (!fullAddress) return "Location N/A";

    const parts = fullAddress.split(',').map(part => part.trim());

    if (parts.length >= 4) {
      const city = parts[1];
      const state = parts[2].replace(/\d+/g, '').trim();
      const country = parts[3];

      return `${city}, ${state}, ${country}`;
    }

    return fullAddress;
  } catch (error) {
    console.error("Error parsing location:", error);
    return "Location N/A";
  }
};


const CrewCard = ({ crewMember, isSelected, onSelect, onViewProfile }) => {
  const [profileImage, setProfileImage] = useState("/images/misc/profile.png");
  const availabilityStatus = crewMember?.is_available ? 'available' : 'busy';
  const availabilityBadge = availabilityBadgeProps[availabilityStatus] || availabilityBadgeProps.available;

  const parsedSkills = JSON.parse(crewMember.skills || "[]");
  const mappedSkills = skillOptions
    .filter(skill => parsedSkills.includes(Number(skill.value)) || parsedSkills.includes(String(skill.value)))
    .map(skill => skill.label);

  const parsedEquipmentsOwned = JSON.parse(crewMember.equipment_ownership || "[]")
  const userRole = roleOptions.find(
    role => role.value === String(crewMember.primary_role)
  );

  useEffect(() => {
    if (crewMember?.crew_member_files?.length > 0) {
      const image = crewMember.crew_member_files.find(file => file.file_type === "profile_photo");

      if (image) {
        if (image.file_path.includes("http")) {
          setProfileImage(image.file_path);
        }
        else {
          const s3FullUrl = `${process.env.REACT_APP_S3_BASE_URL}/${image.file_path}`;
          setProfileImage(s3FullUrl);
        }
      }
    }
  }, [crewMember])

  const matchPercentage = Math.round(crewMember.matchPercentage);

  return (
    <div className="border bg-white rounded-2xl py-4 w-full">
      <div className="flex px-4 gap-4">
        <div className="w-min">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onSelect}
            className={`w-5 h-5 rounded-md border transition-colors data-[state=checked]:bg-button-coloured data-[state=checked]:border-transparent data-[state=unchecked]:bg-background data-[state=unchecked]:border-gray-200`}
          />
        </div>
        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-col md:flex-row items-start justify-start gap-2">
            <img
              src={profileImage}
              alt="Crew Member"
              className="w-[72px] h-[72px] rounded-lg object-cover"
            />
            <div className="flex flex-col gap-1">
              <p className="text-base text-black-primary font-semibold">{crewMember.first_name + " " + crewMember.last_name}</p>
              <p className="text-grey-2 text-sm">{userRole?.label || 'Role'}</p>
              <p className="inline-flex gap-1 items-center text-grey-2 text-sm">
                <Star className="w-4 h-4 text-[#FFB900] fill-[#FFB900]" />
                4.9
              </p>
              <div className="flex md:hidden items-center justify-between gap-1 p-2 rounded-md border border-blue-100 h-8 w-40">
                <ProgressCircle value={matchPercentage} />
                <p className="text-blue-500 text-xs font-bold px-0">{matchPercentage}% AI Match</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge
              icon={<MapPin className="w-4 h-4 text-dark-blue" />}
              alt={"Location pin"}
              text={formatLocation(crewMember.location)}
              textColor={"text-dark-blue"}
              bgColor={"bg-indigo-50"}
            />
            <Badge
              icon={<Route className="w-4 h-4 text-dark-blue" />}
              alt={"Distance icon"}
              text={crewMember.working_distance ? crewMember.working_distance : "Within 10-15 Miles"}
              textColor={"text-dark-blue"}
              bgColor={"bg-indigo-50"}
            />
            {/* default for now */}
            <Badge
              icon={<Aperture className="w-4 h-4 text-dark-blue" />}
              alt={"Projects"}
              text={"147 Projects"}
              textColor={"text-dark-blue"}
              bgColor={"bg-indigo-50"}
            />
          </div>
        </div>
        <div className="hidden md:flex items-center justify-between gap-1 p-2 rounded-md border border-blue-100 h-8 w-40">
          <ProgressCircle value={matchPercentage} />
          <p className="text-blue-500 text-xs font-bold px-0">{matchPercentage}% AI Match</p>
        </div>
      </div>
      <hr />
      <div className="flex flex-col md:flex-row justify-between gap-4 w-full">
        {
          parsedEquipmentsOwned.length > 0 &&
          <FeatureList
            title={"Equipment"}
            list={parsedEquipmentsOwned}
            visibleCount={2}
          />
        }
        {
          mappedSkills.length > 0 &&
          <FeatureList
            title={"Skills"}
            list={mappedSkills}
            visibleCount={2}
          />
        }
      </div>
      <hr />
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="flex gap-2 justify-start">
          <Badge
            icon={<Circle className="w-4 h-4 text-green-900" />}
            text={availabilityBadge.text}
            textColor={availabilityBadge.textColor}
            bgColor={availabilityBadge.bgColor}
          />
          {
            crewMember.is_beige_member == 1 ? (
              <Badge
                icon={<Target className="w-4 h-4 text-orange-600" />}
                alt={"Profile Type"}
                text={"Beige"}
                textColor={"text-orange-600"}
                bgColor={"bg-orange-50"}
              />
            ) : (
              <Badge
                icon={<Target className="w-4 h-4 text-blue-800" />}
                alt={"Profile Type"}
                text={"Memehouse"}
                textColor={"text-blue-800"}
                bgColor={"bg-blue-50"}
              />
            )
          }
        </div>
        {/* <Button className="bg-button-generic text-button-generic-foreground hover:bg-button-generic/60 rounded-lg w-max flex justify-center items-center font-sm py-2"
          onClick={onViewProfile}
        >
          <p className="pl-2 text-sm font-medium">View Profile</p>
          <ChevronRight size={16} />
        </Button> */}
      </div>
    </div>
  )
}

export default CrewCard;
